// Phase 6: User Certificate Portal

import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSupabaseAuth } from '@/hooks/auth';
import { supabase } from '@/integrations/supabase/client';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Award, Download, ExternalLink, CheckCircle } from 'lucide-react';
import { toast } from '@/components/ui/sonner';

const MyCertificates = () => {
  const { user } = useSupabaseAuth();

  const { data: certificates, isLoading } = useQuery({
    queryKey: ['my-certificates', user?.id],
    queryFn: async () => {
      if (!user) return [];

      const { data, error } = await supabase
        .from('certificates')
        .select('*')
        .eq('user_id', user.id)
        .order('issue_date', { ascending: false });

      if (error) throw error;
      return data;
    },
    enabled: !!user
  });

  const copyVerificationLink = (certificate: any) => {
    const link = `${window.location.origin}/verify/${certificate.verification_hash}`;
    navigator.clipboard.writeText(link);
    toast.success('Verification link copied to clipboard');
  };

  if (!user) {
    return (
      <div className="container mx-auto py-8">
        <Card className="p-8 text-center">
          <p className="text-muted-foreground">Please sign in to view your certificates.</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">My Certificates</h1>
        <p className="text-muted-foreground">
          View and manage your earned certificates
        </p>
      </div>

      {isLoading && (
        <Card className="p-8">
          <div className="text-center text-muted-foreground">
            Loading your certificates...
          </div>
        </Card>
      )}

      {!isLoading && (!certificates || certificates.length === 0) && (
        <Card className="p-8 text-center">
          <Award className="h-16 w-16 mx-auto mb-4 text-muted-foreground" />
          <h3 className="text-xl font-semibold mb-2">No Certificates Yet</h3>
          <p className="text-muted-foreground mb-4">
            Complete courses to earn your first certificate!
          </p>
          <Button asChild>
            <a href="/courses">Browse Courses</a>
          </Button>
        </Card>
      )}

      <div className="grid md:grid-cols-2 gap-6">
        {certificates?.map(certificate => (
          <Card key={certificate.id} className="p-6 hover:shadow-lg transition-shadow">
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <Award className="h-5 w-5 text-primary" />
                  <Badge variant={certificate.is_verified ? 'default' : 'secondary'}>
                    {certificate.is_verified ? (
                      <>
                        <CheckCircle className="h-3 w-3 mr-1" />
                        Verified
                      </>
                    ) : 'Pending'}
                  </Badge>
                </div>
                <h3 className="font-bold text-lg mb-1">{certificate.course_title}</h3>
                <p className="text-sm text-muted-foreground mb-2">
                  Issued {new Date(certificate.issue_date).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </p>
                <p className="text-xs font-mono text-muted-foreground">
                  {certificate.certificate_number}
                </p>
              </div>
            </div>

            {certificate.metadata && (certificate.metadata as any).quiz_average && (
              <div className="mb-4 p-3 bg-muted rounded-lg">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Quiz Average</span>
                  <span className="font-bold">{(certificate.metadata as any).quiz_average}%</span>
                </div>
              </div>
            )}

            <div className="flex flex-wrap gap-2">
              {certificate.pdf_url && (
                <Button 
                  size="sm" 
                  variant="default"
                  asChild
                >
                  <a href={certificate.pdf_url} target="_blank" rel="noopener noreferrer">
                    <Download className="h-3 w-3 mr-1" />
                    Download
                  </a>
                </Button>
              )}

              <Button
                size="sm" 
                variant="outline"
                onClick={() => copyVerificationLink(certificate)}
              >
                <ExternalLink className="h-3 w-3 mr-1" />
                Verify Link
              </Button>
            </div>

            {!certificate.pdf_url && certificate.email_sent && (
              <p className="text-xs text-muted-foreground mt-3">
                📧 Certificate PDF sent to your email
              </p>
            )}
          </Card>
        ))}
      </div>
    </div>
  );
};

export default MyCertificates;
